import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config";
import { ConnectExplain, Connected, Message, Waitlisted } from "./screens";

// What Google's round trip came back with (`?result=` from the callback).
const NOTICES = {
  cancelled:
    "You didn't allow access, so nothing was connected. You can try again anytime.",
  scope:
    "Google didn't give us access to Gmail. On Google's screen, keep the Gmail box ticked.",
  error: "Something went wrong connecting Gmail. Please try again.",
};

/*
 * A carrier connects Gmail (read-only) so we pick up broker rate cons: what
 * we read and never do, then Google's consent, then "Gmail connected".
 * Without the Google client set up, "Join the waitlist" records the opt-in.
 */
export default function GmailConnect() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [screen, setScreen] = useState("loading");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  const url = useMemo(() => `${API_URL}/gmail-connect/${token}`, [token]);
  const { src, result } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      src: params.get("src") || undefined,
      result: params.get("result"),
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    // A refresh shouldn't show Google's result again.
    if (result) {
      const clean = new URL(window.location.href);
      clean.searchParams.delete("result");
      window.history.replaceState(null, "", clean.toString());
    }
    axios
      .get(url, { params: { src } })
      .then(({ data: d }) => {
        if (cancelled) return;
        setData(d);
        if (d.connected) setScreen("connected");
        else if (NOTICES[result]) {
          setNotice(NOTICES[result]);
          setScreen("explain");
        } else setScreen(d.waitlisted ? "waitlisted" : "explain");
      })
      .catch(() => {
        if (!cancelled) setScreen("error");
      });
    return () => {
      cancelled = true;
    };
  }, [url, src, result]);

  async function connect() {
    setBusy(true);
    setNotice("");
    try {
      const { data: d } = await axios.post(`${url}/start`, { src });
      if (d.auth_url) {
        // Off to Google; the callback brings the carrier back here.
        window.location.assign(d.auth_url);
        return;
      }
      setScreen("waitlisted");
    } catch {
      setNotice(NOTICES.error);
    }
    setBusy(false);
  }

  async function disconnect() {
    setBusy(true);
    try {
      const { data: d } = await axios.post(`${url}/disconnect`, { src });
      setData(d);
      setNotice("");
      setScreen("explain");
    } catch {
      setNotice("We couldn't disconnect Gmail. Please try again.");
    }
    setBusy(false);
  }

  const companyName = data?.company_name || "";

  if (screen === "loading") return <Message title="Loading…" />;
  if (screen === "error") {
    return (
      <Message
        title="Something's not right"
        body="We couldn't open this link. Text us and we'll send a new one."
      />
    );
  }
  if (screen === "connected") {
    return (
      <>
        {notice && (
          <p
            role="alert"
            className="fixed inset-x-0 top-0 z-30 m-0 bg-rose-600 px-4 py-2 text-center text-[14px] text-white"
          >
            {notice}
          </p>
        )}
        <Connected
          gmailAddress={data?.gmail_address}
          busy={busy}
          onDisconnect={disconnect}
        />
      </>
    );
  }
  if (screen === "waitlisted") return <Waitlisted companyName={companyName} />;
  return (
    <ConnectExplain
      companyName={companyName}
      notice={notice}
      busy={busy}
      onConnect={connect}
      waitlist={data?.oauth_ready === false}
    />
  );
}
